/**
 * Deterministic position facts for the AI coach. Everything here is computed
 * with chess.js from the FEN and from engine lines that are re-validated move by
 * move, so the language model is handed facts it can narrate but cannot invent.
 */
import { Chess, type Color, type Move, type PieceSymbol, type Square } from "chess.js";
import { formatScore, type Score } from "./uci";
import { classifyMove, winPercent, type Classification } from "./winchance";

export const PIECE_NAME: Record<PieceSymbol, string> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};
export const PIECE_VALUE: Record<PieceSymbol, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
const COLOR_NAME: Record<Color, string> = { w: "White", b: "Black" };

export type EngineFacts = {
  /** Evaluation of the position before the move, White's point of view, with the engine's best line. */
  before?: { score: Score; pv?: string[] };
  /** Evaluation after the move that was played, White's point of view, with the best reply line. */
  after?: { score: Score; pv?: string[] };
};

export type LoosePiece = { square: Square; piece: string; attackers: string[]; defended: boolean };

export type Facts = {
  fen: string;
  sideToMove: string;
  moveNumber: number;
  phase: "opening" | "middlegame" | "endgame";
  material: { white: number; black: number; summary: string };
  inCheck: boolean;
  legalMoveCount: number;
  openingName?: string;
  move?: {
    san: string;
    uci: string;
    mover: string;
    piece: string;
    from: Square;
    to: Square;
    captured?: string;
    promotion?: string;
    check: boolean;
    mate: boolean;
    castle?: "kingside" | "queenside";
    enPassant: boolean;
  };
  /** Mover's pieces left loose after the move (or, with no move, the side to move's). */
  loosePieces: LoosePiece[];
  /** Captures the opponent could make next move that win material. */
  freeCaptures: { san: string; gains: string }[];
  engine?: {
    evalBefore?: string;
    evalAfter?: string;
    moverWinBefore?: number;
    moverWinAfter?: number;
    classification?: Classification;
    bestMoveSan?: string;
    bestLineSan?: string[];
    replyLineSan?: string[];
    playedWasBest?: boolean;
  };
  result?: string;
};

/** Converts a UCI PV to SAN, stopping at the first illegal move. */
export function pvToSan(fen: string, pv: string[] | undefined, max = 8): string[] {
  if (!pv?.length) return [];
  const c = new Chess(fen);
  const out: string[] = [];
  for (const uci of pv.slice(0, max)) {
    try {
      const m = c.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] as PieceSymbol | undefined });
      out.push(m.san);
    } catch {
      break;
    }
  }
  return out;
}

function material(c: Chess) {
  let white = 0;
  let black = 0;
  const counts: Record<Color, Record<PieceSymbol, number>> = {
    w: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 },
    b: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 },
  };
  for (const row of c.board())
    for (const sq of row) {
      if (!sq) continue;
      counts[sq.color][sq.type]++;
      if (sq.color === "w") white += PIECE_VALUE[sq.type];
      else black += PIECE_VALUE[sq.type];
    }
  const side = (col: Color) =>
    (["q", "r", "b", "n", "p"] as PieceSymbol[])
      .filter((t) => counts[col][t] > 0)
      .map((t) => `${counts[col][t]}${t.toUpperCase()}`)
      .join(" ");
  return { white, black, summary: `White ${side("w") || "K"} vs Black ${side("b") || "K"}`, counts };
}

function countPawns(c: Chess) {
  let n = 0;
  for (const row of c.board()) for (const sq of row) if (sq?.type === "p") n++;
  return n;
}

function phaseOf(c: Chess, white: number, black: number): Facts["phase"] {
  const nonPawn = white + black - countPawns(c);
  if (c.moveNumber() <= 10 && nonPawn >= 60) return "opening";
  if (nonPawn <= 26) return "endgame";
  return "middlegame";
}

/** Pieces of `color` that are attacked and either undefended or attacked by something cheaper. */
export function loosePieces(c: Chess, color: Color): LoosePiece[] {
  const them: Color = color === "w" ? "b" : "w";
  const out: LoosePiece[] = [];
  for (const row of c.board())
    for (const sq of row) {
      if (!sq || sq.color !== color || sq.type === "k") continue;
      const attackers = c.attackers(sq.square, them);
      if (attackers.length === 0) continue;
      const defended = c.attackers(sq.square, color).length > 0;
      const cheapest = Math.min(...attackers.map((a) => PIECE_VALUE[c.get(a)!.type] || 100));
      if (!defended || cheapest < PIECE_VALUE[sq.type]) {
        out.push({
          square: sq.square,
          piece: PIECE_NAME[sq.type],
          attackers: attackers.map((a) => `${PIECE_NAME[c.get(a)!.type]} on ${a}`),
          defended,
        });
      }
    }
  return out;
}

/** Captures for the side to move that win material by a simple exchange count. */
export function profitableCaptures(c: Chess): { san: string; gains: string }[] {
  const us = c.turn();
  const them: Color = us === "w" ? "b" : "w";
  const out: { san: string; gains: string; v: number }[] = [];
  for (const m of c.moves({ verbose: true }) as Move[]) {
    if (!m.captured) continue;
    const gain = PIECE_VALUE[m.captured];
    const defended = c.attackers(m.to, them).length > 0;
    const net = defended ? gain - PIECE_VALUE[m.piece] : gain;
    if (net > 0)
      out.push({
        san: m.san,
        gains: `${PIECE_NAME[m.captured]}${defended ? " (defended, but worth more than the capturing piece)" : " for free"}`,
        v: net,
      });
  }
  return out
    .sort((a, b) => b.v - a.v)
    .slice(0, 4)
    .map(({ san, gains }) => ({ san, gains }));
}

export function buildFacts(input: { fen: string; moveSan?: string; engine?: EngineFacts; openingName?: string }): Facts {
  const before = new Chess(input.fen);
  const legalMoveCount = before.moves().length;
  const mover = before.turn();
  let move: Facts["move"];
  let after = before;
  if (input.moveSan) {
    after = new Chess(input.fen);
    const m = after.move(input.moveSan);
    move = {
      san: m.san,
      uci: m.lan,
      mover: COLOR_NAME[mover],
      piece: PIECE_NAME[m.piece],
      from: m.from,
      to: m.to,
      captured: m.captured ? PIECE_NAME[m.captured] : undefined,
      promotion: m.promotion ? PIECE_NAME[m.promotion] : undefined,
      check: after.inCheck(),
      mate: after.isCheckmate(),
      castle: m.isKingsideCastle() ? "kingside" : m.isQueensideCastle() ? "queenside" : undefined,
      enPassant: m.isEnPassant(),
    };
  }
  const mat = material(after);
  const facts: Facts = {
    fen: input.fen,
    sideToMove: COLOR_NAME[mover],
    moveNumber: before.moveNumber(),
    phase: phaseOf(after, mat.white, mat.black),
    material: { white: mat.white, black: mat.black, summary: mat.summary },
    inCheck: before.inCheck(),
    legalMoveCount,
    openingName: input.openingName,
    move,
    loosePieces: loosePieces(after, mover),
    freeCaptures: move ? profitableCaptures(after) : [],
  };

  // Without a move, "after" is the mated position itself and the mover is the side mated.
  if (after.isCheckmate()) facts.result = `${COLOR_NAME[move ? mover : mover === "w" ? "b" : "w"]} delivers checkmate.`;
  else if (after.isStalemate()) facts.result = "Stalemate: the game is drawn.";
  else if (after.isInsufficientMaterial()) facts.result = "Draw by insufficient material.";

  const e = input.engine;
  if (e) {
    const bestLine = pvToSan(input.fen, e.before?.pv);
    const replyLine = move ? pvToSan(after.fen(), e.after?.pv) : [];
    const moverWin = (s?: Score) => {
      if (!s) return undefined;
      const w = winPercent(s);
      return mover === "w" ? w : 100 - w;
    };
    const moverWinBefore = moverWin(e.before?.score);
    const moverWinAfter = moverWin(e.after?.score);
    const playedWasBest = !!(move && e.before?.pv?.[0] && e.before.pv[0] === move.uci);
    facts.engine = {
      evalBefore: e.before ? formatScore(e.before.score) : undefined,
      evalAfter: e.after ? formatScore(e.after.score) : undefined,
      moverWinBefore: moverWinBefore === undefined ? undefined : Math.round(moverWinBefore),
      moverWinAfter: moverWinAfter === undefined ? undefined : Math.round(moverWinAfter),
      bestMoveSan: bestLine[0],
      bestLineSan: bestLine.length ? bestLine : undefined,
      replyLineSan: replyLine.length ? replyLine : undefined,
      playedWasBest,
      classification:
        move && moverWinBefore !== undefined && moverWinAfter !== undefined
          ? classifyMove({
              moverWinBefore,
              moverWinAfter,
              playedUci: move.uci,
              bestUci: e.before?.pv?.[0],
              legalMoveCount,
            })
          : undefined,
    };
  }
  return facts;
}

/** Renders facts as a compact, model-friendly block. */
export function factsToText(f: Facts): string {
  const L: string[] = [];
  L.push(`FEN before the move: ${f.fen}`);
  L.push(`Side to move: ${f.sideToMove}, move ${f.moveNumber}, phase: ${f.phase}${f.inCheck ? ", in check" : ""}`);
  if (f.openingName) L.push(`Opening: ${f.openingName}`);
  L.push(`Material${f.move ? " after the move" : ""}: ${f.material.summary} (points ${f.material.white}-${f.material.black})`);
  if (f.move) {
    const m = f.move;
    const extras = [
      m.captured && `captures a ${m.captured}`,
      m.castle && `castles ${m.castle}`,
      m.enPassant && "en passant capture",
      m.promotion && `promotes to a ${m.promotion}`,
      m.mate ? "checkmate" : m.check ? "gives check" : "",
    ].filter(Boolean);
    L.push(`Move played: ${m.mover} ${m.san} (${m.piece} ${m.from}-${m.to}${extras.length ? ", " + extras.join(", ") : ""})`);
  }
  if (f.result) L.push(`Result: ${f.result}`);
  if (f.loosePieces.length)
    L.push(
      `Loose ${f.sideToMove} pieces${f.move ? " after the move" : ""}: ` +
        f.loosePieces
          .map(
            (p) =>
              `${p.piece} on ${p.square} (attacked by ${p.attackers.join(", ")}; ${p.defended ? "defended, but attacked by a cheaper piece" : "undefended"})`,
          )
          .join("; "),
    );
  if (f.freeCaptures.length)
    L.push(`Opponent captures that win material next move: ${f.freeCaptures.map((c) => `${c.san} wins a ${c.gains}`).join("; ")}`);
  const e = f.engine;
  if (e) {
    if (e.evalBefore)
      L.push(
        `Engine eval before (White POV): ${e.evalBefore}${e.moverWinBefore !== undefined ? `, ${f.sideToMove}'s winning chances ${e.moverWinBefore}%` : ""}`,
      );
    if (e.evalAfter)
      L.push(
        `Engine eval after the move (White POV): ${e.evalAfter}${e.moverWinAfter !== undefined ? `, ${f.sideToMove}'s winning chances ${e.moverWinAfter}%` : ""}`,
      );
    if (e.classification) L.push(`Classification: ${e.classification}`);
    if (e.bestLineSan) L.push(`Engine best line from the position before the move: ${e.bestLineSan.join(" ")}`);
    if (f.move && e.playedWasBest) L.push("The move played was the engine's top choice.");
    if (e.replyLineSan) L.push(`Engine's best continuation after the move played: ${e.replyLineSan.join(" ")}`);
  }
  return L.join("\n");
}

/** Plain-language fallback used when no language model is configured. */
export function templateExplanation(f: Facts): string {
  const parts: string[] = [];
  const e = f.engine;
  if (f.move) {
    parts.push(`${f.move.mover} played ${f.move.san}${e?.classification ? `, classified as ${e.classification}` : ""}.`);
    if (f.result) parts.push(f.result);
    if (e?.playedWasBest) parts.push("It matches the engine's first choice.");
    else if (e?.bestMoveSan)
      parts.push(`The engine preferred ${e.bestMoveSan}${e.bestLineSan ? `, continuing ${e.bestLineSan.slice(0, 5).join(" ")}` : ""}.`);
    if (e?.moverWinBefore !== undefined && e?.moverWinAfter !== undefined && e.moverWinBefore - e.moverWinAfter >= 5)
      parts.push(`Winning chances went from ${e.moverWinBefore}% to ${e.moverWinAfter}%.`);
    if (f.freeCaptures.length)
      parts.push(`Watch out: the opponent can now play ${f.freeCaptures[0].san}, winning a ${f.freeCaptures[0].gains}.`);
    else if (f.loosePieces.length) parts.push(`The ${f.loosePieces[0].piece} on ${f.loosePieces[0].square} is loose.`);
    if (e?.replyLineSan) parts.push(`Best play from here: ${e.replyLineSan.slice(0, 5).join(" ")}.`);
  } else {
    parts.push(`${f.sideToMove} to move in the ${f.phase}. ${f.material.summary}.`);
    if (e?.evalBefore) parts.push(`The engine evaluates the position at ${e.evalBefore}.`);
    if (e?.bestLineSan) parts.push(`Its main line: ${e.bestLineSan.slice(0, 6).join(" ")}.`);
    if (f.loosePieces.length) parts.push(`Loose pieces: ${f.loosePieces.map((p) => `${p.piece} on ${p.square}`).join(", ")}.`);
  }
  return parts.join(" ");
}
